import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useRef } from "react";
import { useAffiliateSelection } from "@/auth/affiliateSelection";
import { useToast } from "@rumbledirect/ui-v2";
import { downloadDossierExport } from "@/dossiers/downloadDossierExport";

interface DossierExportSearch {
  // The affiliate the export was requested in, set by the export-ready email
  // link so a multi-affiliate user lands on the right tenant.
  affiliateId?: string;
}

export const Route = createFileRoute(
  "/_authenticated/dossiers_/exports_/$exportId",
)({
  validateSearch: (search: Record<string, unknown>): DossierExportSearch => ({
    affiliateId:
      typeof search.affiliateId === "string" ? search.affiliateId : undefined,
  }),
  component: DossierExportDownloadPage,
});

function DossierExportDownloadPage(): ReactNode {
  const { exportId } = Route.useParams();
  const { affiliateId: exportAffiliateId } = Route.useSearch();
  const { affiliateId, availableAffiliateIds, selectAffiliate } =
    useAffiliateSelection();
  const navigate = useNavigate();
  const toast = useToast();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    if (exportAffiliateId !== undefined) {
      // The email link names the owning tenant; refuse to download it under
      // another one.
      if (!availableAffiliateIds.includes(exportAffiliateId)) {
        toast.error("Je hebt geen toegang tot deze export.");
        void navigate({ to: "/dashboard" });
        return;
      }
      // selectAffiliate publishes the tenant to the auth store synchronously,
      // so the download below already targets the right affiliate.
      if (exportAffiliateId !== affiliateId) {
        selectAffiliate(exportAffiliateId);
      }
    }

    toast.success("De export wordt gedownload.");

    void downloadDossierExport(exportId)
      .catch(() => {
        toast.error("Downloaden van de export is mislukt.");
      })
      .finally(() => {
        void navigate({ to: "/dashboard" });
      });
  }, [
    exportId,
    exportAffiliateId,
    affiliateId,
    availableAffiliateIds,
    selectAffiliate,
    navigate,
    toast,
  ]);

  return (
    <p role="status" aria-live="polite">
      De export wordt gedownload.
    </p>
  );
}
